import { AdobeAnalyticsService } from './services/adobe-analytics/adobe-analytics.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, SecurityContext, ChangeDetectorRef } from '@angular/core';
import { PortfolioDataService } from './services/portfolio-data-service/portfolio-data.service';
import { Observable, filter, Subject, lastValueFrom } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { WindowRef } from './services/window-ref/window-ref.service';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { LoggerCode } from './models/logger';
import { LoggerService } from './services/logger/logger.service';
import { targetAssetMixRoutes } from './models/chart-models/target-allocation';
import { DrawerComponent, DrawerTrackerService } from '@vanguard/pfx-components';
import { environment } from 'src/environments/environment';
import { DrawerPageSortMapper } from './config/config.inline-drawer';
import { AemDrawerData } from '@vanguard/pfx-components/lib/components/drawer/drawer-data.interface';
import { default as helpContent } from '../assets/content.help.json';
import { CustomUserModernizationService } from './services/custom-user-modernization/custom-user-modernization.service';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { MFEEventTypes } from '@vanguard/mfe-events';
import { PortfolioHealthCheckService } from './services/portfolio-health-check/portfolio-health-check.service';
import { ChannelBrokerService } from './services/channel-broker/channel-broker.service';
import { PhcUrl } from './models/interfaces/portfolio-health-check-data';
import { PageHelpComponent } from './components/page-help/page-help.component';
import { PageHelpService } from './components/page-help/page-help.component.service';
import { Renderer2 } from '@angular/core';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {

  public isLoading: Observable<boolean>;
  public isFailure: Observable<boolean>;
  public isCBFailure: Observable<boolean>;
  public showPWHeader: Observable<boolean>;
  isTargetAssetMixPage: boolean = false;
  @ViewChild('pageHelpDrawer') pageHelpDrawer: DrawerComponent;
  drawerHelpContentUrl: string;
  pageHelpDrawerPosition: string = 'end';
  shouldFocusPanelOpen: boolean = true;
  contingencyHelpData: AemDrawerData = helpContent;
  fpDrawerThreshold: number = 1200;
  isFpDrawerModal: boolean = false;
  fpDrawerWidth: string = '392px';
  focusPanelMFELoaded: boolean = false;
  private readonly _destroy: Subject<void> = new Subject<void>();
  currentUrl: string;
  isPageHelpOpen: boolean = false;
  customDisplayName: string = '';
  isFPOpen: boolean = false;
  readonly CLASSNAME_FOCUS_PANEL_ACTIVE = 'ss-panel--active';
  isPageHelpVisible = false;
  @ViewChild('pageHelpDrawerComponent') pageHelpDrawerComponent: PageHelpComponent;
  isAccountSelected: boolean = false;
  private isKeyboardNavigation = false;
  hideHeaderAndFooter = false;

  // eslint-disable-next-line max-params
  constructor(
    private titleService: Title,
    private portfolioDataService: PortfolioDataService,
    public router: Router,
    private windowRef: WindowRef,
    private meta: Meta,
    private loggerService: LoggerService,
    public readonly pageHelpDrawerTrackerService: DrawerTrackerService,
    private adobeAnalyticsService: AdobeAnalyticsService,
    private customUserModernizationService: CustomUserModernizationService, // do not remove this; it will initialize the user modernization verification.
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private changeDetector: ChangeDetectorRef,
    private pw3ApiService: PortfolioHealthCheckService,
    private cbService: ChannelBrokerService,
    public readonly pageHelpService: PageHelpService,
    private renderer: Renderer2,
  ) {
    this.hideHeaderAndFooter = this.isVGMobileWebview()
    this.drawerHelpContentUrl = '../assets/content.help.json';

    this.pw3ApiService.setShowPWHeader(true)

    this.isCBFailure = this.cbService.getFailure();

    this.isLoading = this.pw3ApiService.getLoading();
    this.isFailure = this.pw3ApiService.getFailure();
    this.showPWHeader = this.pw3ApiService.getShowPWHeader();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.windowRef.nativeWindow?.scrollTo({ top: 0, left: 0 });
        this.loggerService.info(
          {
            message: 'PageLoadSuccess',
            path: this.sanitizer.sanitize(SecurityContext.URL, event.url) || '',
          },
          LoggerCode.PAGE_LOAD,
        );

        this.currentUrl = event.url;

        this.isLoading = this.pw3ApiService.getLoading();
        this.isFailure = this.pw3ApiService.getFailure();
        this.showPWHeader = this.pw3ApiService.getShowPWHeader();

        this.isTargetAssetMixPage = false;
        this.toggleFocusPanel(false);

        let assetMixRoutes = Object.values(targetAssetMixRoutes) as Array<string>;
        this.isTargetAssetMixPage = assetMixRoutes.includes(event.url);
        if (this.autoCloseDrawer()) {
          if (this.isFPOpen) {
            this.shouldFocusPanelOpen = true;
            this.closeFocusPanel();
          }
        } else {
          if (this.shouldFocusPanelOpen && !this.isPageHelpOpen) {
            this.toggleFocusPanel(true)
          }
        }
      });

    this.renderer.listen('window', 'keydown', () => {
      this.isKeyboardNavigation = true;
    });

    this.renderer.listen('window', 'mousedown', () => {
      this.isKeyboardNavigation = false;
    });

    window.addEventListener('load', (event) => {
      this.loggerService.info(
        {
          message: 'Portfolio health check load duration',
          duration: event.timeStamp,
        },
        LoggerCode.PORTFOLIO_WATCH_LOADED_DURATION,
      );
    });
  }

  autoCloseDrawer(): boolean {
    const excludeUrls = [
      '/target-mix-custom-view',
      '/tester-tool',
      '/target-mix-about',
      '/target-mix-questionnaire',
      '/suggested-target-asset-mix',
    ];
    if (this.router.url.startsWith('/hypothetical')) {
      excludeUrls.push(this.currentUrl);
    }
    return excludeUrls.includes(this.currentUrl);
  }

  ngOnInit() {
    this.updateSpoid();
    this.setRandomNum();

    this.isLoading.subscribe(isLoading => {
      if (!isLoading) {
        window.dispatchEvent(new Event('resize'));
      }
    })
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Update the title based on the current route
      const newTitle = this.getTitleForRoute(event.urlAfterRedirects);
      this.titleService.setTitle(newTitle);
      this.resetTabIndex();
    });

  }

  ngAfterViewInit() {
    this.pageHelpService.currentDrawerStatus$.subscribe((isPageHelpOpen) => {
      this.isPageHelpVisible = isPageHelpOpen ? true : false;
    });
    this.changeDetector.detectChanges();
  }

  onDrawerStateChange(event) {
    if (!event?.isDrawerOpen && this.isPageHelpVisible) {
      this.pageHelpService.setCurrentDrawerStatus(false);
    }
  }

  getTitleForRoute(url: string): string {
    switch (url) {
      case '/':
        return 'Portfolio Analysis | Vanguard ';
      case '/asset-mix':
        return 'Asset Mix | Vanguard';
      case '/stock-analysis/market-capitalization':
        return 'Stock Analysis | Vanguard';
      case '/bond-analysis/credit-quality':
        return 'Bond Analysis | Vanguard';
      case '/costs/mutual-fund-costs':
        return 'Investment Costs | Vanguard';
      case '/tester-tool':
        return 'Portfolio Tester | Vanguard';
      default:
        return 'Portfolio Analysis | Vanguard';
    }
  }

  navigateToTesterPage() {
    this.router.navigate(['/tester-tool']);
  }

  /*
  Used for Medallia
   */
  private updateSpoid(): void {
    const spoid = this.meta.getTag('name="DCSext.sPoid"')?.content;
    const window = this.windowRef.nativeWindow;
    window.spoid = spoid ? spoid : -1;
  }

  private setRandomNum(): void {
    const randomNumber = Math.floor(Math.random() * 4) + 1;
    const window = this.windowRef.nativeWindow;
    window.randomNum = randomNumber ? randomNumber : -1;
  }

  handleOpenFpDrawer(): void {
    this.pageHelpDrawerTrackerService.closeDrawer();
    setTimeout(() => {
      this.toggleFocusPanel(true)
      this.adobeAnalyticsService.CreateCTATrigger(
        'Secure Site Portfolio Watch',
        'Focus Panel Show Button',
        'Button',
      );
    }, 230);
  }

  getGroupData(event: any): void {
    this.customDisplayName = event.detail.fullDisplayName;
    this.isAccountSelected = event.detail.accounts.some(acc => acc.accountName === event.detail.fullDisplayName)
    this.portfolioDataService.setIsAccountSeleted(this.isAccountSelected)
    if (this.isAccountSelected) {
      this.portfolioDataService.setAccountSeleted(event?.detail?.accounts[0]?.accountId)
    }
    this.portfolioDataService.setSelectedAccountGroupDetails({
      GroupName: event.detail.fullDisplayName,
      ViewID: event.detail.clientViewId,
      IsGroup: true,
    });
    this.portfolioDataService.loadChartDataPayload();
  }

  checkWindowWidth() {
    this.toggleFocusPanel(window?.matchMedia(`(min-width: ${this.fpDrawerThreshold}px)`)?.matches || false)
  }

  closeFocusPanel(data?: any) {
    this.toggleFocusPanel(false)
    if (data?.analyticsData) {
      this.adobeAnalyticsService.CreateCTATrigger(
        data.analyticsData.location,
        data.analyticsData.ctaName,
        data.analyticsData.ctaType,
      );
    }
  }

  toggleFocusPanel(open: boolean) {

    const bodyElement = document.querySelector('body');

    bodyElement.classList.remove(this.CLASSNAME_FOCUS_PANEL_ACTIVE)
    this.isFPOpen = false;
    document.body.dispatchEvent(new CustomEvent('ss-panel-visibility', { detail: { open: this.isFPOpen } }));
  }

  async loadSelectedFocusPanelItem(): Promise<void> {
    try {
      const detail = await (this.getSelectedFocusPanelItem())
      this.getGroupData({ detail });
    } catch (error) {
      console.log('Response error: ', error)
    }
  }

  getSelectedFocusPanelItem() {
    return lastValueFrom(this.http.get(environment.FOCUS_PANEL_GET_SELECTED_GROUP, { withCredentials: true }))
  }

  resetTabIndex() {
    const mainElement = document.querySelector('.modernized-wrapper');
    if (mainElement) {
      this.renderer.setAttribute(mainElement, 'tabindex', '-1');
      setTimeout(() => {
        if (!this.isKeyboardNavigation) {
          (mainElement as HTMLElement).focus();
          setTimeout(() => {
            (mainElement as HTMLElement).blur();
          }, 150)
        }
      }, 250);
    }
  }

  /**
   * Function created to check if our webpage is rendering in a mobile device app in a webview.
   * Vanguard customize their userAgent, this are some examples:
   * Android: Beacon/10.70.0 (Android 10.4.1; iPad3,4)
   * IOS: Beacon/99.99.99 (IOS 10.4.1; iPad3,4)
  */
  isVGMobileWebview() {
    //@ts-ignore
    const userAgent = (navigator?.userAgent || navigator?.vendor || window?.opera)?.toLowerCase();
    const poid = (window as any).spoid

    if (!userAgent) {
      this.loggerService.error({
        poid,
        message: `Unable to retrieve the browser UserAgent.`,
        userAgent
      }, LoggerCode.NO_USERAGENT)
      return false;
    }

    this.loggerService.info({
      poid,
      message: `UserAgent successfully retrieved`,
      userAgent
    }, LoggerCode.USERAGENT)

    // Check for custom Vanguard WebView UserAgent
    if (!/beacon/.test(userAgent)) {
      this.loggerService.info({
        poid,
        message: `Custom Vanguard WebView UserAgent not detected.`,
        userAgent
      }, LoggerCode.NO_VG_WEBVIEW_USERAGENT)
      return false;
    }

    this.loggerService.info({
      poid,
      message: `Custom Vanguard WebView UserAgent detected.`,
      userAgent
    }, LoggerCode.VG_WEBVIEW_USERAGENT)

    // Check for mobile device
    if (/ios|android|ipad|iphone|ipod/.test(userAgent)) {
        this.loggerService.info({
          poid,
          message: `Mobile device detected via UserAgent.`,
          userAgent
        }, LoggerCode.MOBILE_DEVICE)
        return true;
    }

    this.loggerService.info({
      poid,
      message: `Desktop device detected via UserAgent.`,
      userAgent
    }, LoggerCode.DESKTOP_DEVICE)

    return false;
  }
}
